// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import isNumeric from "@insite/client-framework/Common/isNumeric";
import breakpoints from "@rugby/XDL/PreStyle/breakpoints";

class BreakpointNotFoundError extends Error {
    constructor(breakpoint: string) {
        super(`Breakpoint '${breakpoint}' not found`);
    }
}

const defaultSizes: { [key: string]: number } = {};
breakpoints.keys.forEach((item: string, index: number) => {
    defaultSizes[item] = breakpoints.values[index];
});

class CustomMediaQuery {
    sizes: any;
    constructor(sizes: any = defaultSizes) {
        this.sizes = { ...defaultSizes, ...sizes };
    }

    _isBreakpoint(breakpoint: string) {
        return Boolean(this.sizes[breakpoint]);
    }

    _next(breakpoint: string): string {
        const keys = Object.keys(this.sizes);
        const index = keys.indexOf(breakpoint);

        return keys[index + 1];
    }

    gt(breakpoint: string | number) {
        let value = breakpoint;
        if (!isNumeric(breakpoint.toString())) {
            if (!this._isBreakpoint(breakpoint.toString())) {
                throw new BreakpointNotFoundError(breakpoint.toString());
            }
            value = this.sizes[breakpoint] + 1;
        }

        return `@media only screen and (min-width: ${value}px)`;
    }

    gte(breakpoint: string | number) {
        let value = breakpoint;
        if (!isNumeric(breakpoint.toString())) {
            if (!this._isBreakpoint(breakpoint.toString())) {
                throw new BreakpointNotFoundError(breakpoint.toString());
            }
            value = this.sizes[breakpoint];
        }

        return `@media only screen and (min-width: ${value}px)`;
    }

    // The max-width value is calculated as the next breakpoint less 0.02px.
    // See https://www.w3.org/TR/mediaqueries-4/#mq-min-max
    // Uses 0.02px rather than 0.01px to work around a current rounding bug
    // in Safari. See https://bugs.webkit.org/show_bug.cgi?id=178261
    lte(breakpoint: string | number) {
        let value = breakpoint;
        if (!isNumeric(breakpoint.toString())) {
            if (!this._isBreakpoint(breakpoint.toString())) {
                throw new BreakpointNotFoundError(breakpoint.toString());
            }
            value = this.sizes[breakpoint] - 0.02;
        }
        return `@media only screen and (max-width: ${value}px)`;
    }

    lt(breakpoint: string | number) {
        let value = breakpoint;
        if (!isNumeric(breakpoint.toString())) {
            if (!this._isBreakpoint(breakpoint.toString())) {
                throw new BreakpointNotFoundError(breakpoint.toString());
            }
            value = this.sizes[breakpoint] - 1;
        }
        return `@media only screen and (max-width: ${value}px)`;
    }

    between(min: string | number, max: string | number) {
        let minValue = min;
        let maxValue = max;
        if (!isNumeric(min.toString())) {
            if (!this._isBreakpoint(min.toString())) {
                throw new BreakpointNotFoundError(min.toString());
            }
            minValue = this.sizes[min];
        }

        if (!isNumeric(max.toString())) {
            if (!this._isBreakpoint(max.toString())) {
                throw new BreakpointNotFoundError(max.toString());
            }
            maxValue = this.sizes[max] - 0.02;
        }

        return `@media only screen and (min-width: ${minValue}px) and
			(max-width: ${maxValue}px)`;
    }

    only(breakpoint: string) {
        const nextBreakpoint = this._next(breakpoint);

        return this._isBreakpoint(nextBreakpoint) ? this.between(breakpoint, nextBreakpoint) : this.gte(breakpoint);
    }
}

export default (sizes?: any) => new CustomMediaQuery(sizes);

export const MediaQuery = new CustomMediaQuery();
